import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Ingredient, Language, Recipe } from '../types';
import XIcon from './icons/XIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import { getTranslation, isRTL } from '../utils/translations';
import CookingQuantityCapsule from './CookingQuantityCapsule';

interface RecipeIngredientsModalProps {
    isOpen: boolean;
    onClose: () => void;
    recipe: Recipe;
    servings: number;
    language?: Language;
}

const RecipeIngredientsModal: React.FC<RecipeIngredientsModalProps> = ({
    isOpen,
    onClose,
    recipe,
    servings,
    language,
}) => {
    const t = (key: string) => getTranslation(language, key);
    const rtl = isRTL(language);
    const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

    const groupedIngredients = useMemo(() => {
        const groups: Record<string, Ingredient[]> = {};
        recipe.ingredients.forEach((ing) => {
            const section = ing.section?.trim() || t('ingredients');
            if (!groups[section]) groups[section] = [];
            groups[section].push(ing);
        });
        return Object.entries(groups);
    }, [recipe.ingredients, language]);

    const scalingFactor = servings / (recipe.servings || 1);

    const toggleSection = (section: string) => {
        setCollapsedSections((prev) => {
            const next = new Set(prev);
            if (next.has(section)) next.delete(section);
            else next.add(section);
            return next;
        });
    };

    if (!isOpen) return null;

    return createPortal(
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[350] flex items-center justify-center p-4 animate-fade-in"
            onClick={onClose}
        >
            <div
                className="bg-white dark:bg-slate-900 w-full max-w-md max-h-[min(85dvh,calc(100dvh-2rem))] rounded-[2.5rem] shadow-2xl flex flex-col overflow-hidden border border-white/20 dark:border-slate-800"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-6 pt-6 pb-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center shrink-0">
                    <div className="min-w-0 pr-4">
                        <h3 className="text-lg font-black text-slate-800 dark:text-slate-100 tracking-tight truncate">
                            {t('ingredients')}
                        </h3>
                        <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mt-0.5 truncate">
                            {recipe.name}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 text-slate-300 dark:text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition-colors bg-slate-50 dark:bg-slate-800 rounded-full shrink-0"
                    >
                        <XIcon className="w-6 h-6" />
                    </button>
                </div>

                <div className="flex-grow min-h-0 overflow-y-auto scrollbar-none px-4 py-4 space-y-3">
                    {groupedIngredients.length === 0 ? (
                        <p className="text-center text-sm text-slate-400 dark:text-slate-500 py-8">
                            {t('ingredients')}
                        </p>
                    ) : (
                        groupedIngredients.map(([section, ings]) => {
                            const isCollapsed = collapsedSections.has(section);
                            const translatedSection = t(section.toLowerCase());
                            const displaySection = translatedSection !== section.toLowerCase()
                                ? translatedSection
                                : section;

                            return (
                                <div
                                    key={section}
                                    className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden"
                                >
                                    <button
                                        type="button"
                                        onClick={() => toggleSection(section)}
                                        className="w-full flex items-center justify-between gap-3 p-4 hover:bg-slate-100/80 dark:hover:bg-slate-800 transition-colors"
                                    >
                                        <div className="flex items-center gap-2 min-w-0">
                                            <ChevronDownIcon
                                                className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${isCollapsed ? 'ltr:-rotate-90 rtl:rotate-90' : ''}`}
                                            />
                                            <span className="text-xs font-black text-brand-orange uppercase tracking-[0.15em] truncate">
                                                {displaySection}
                                            </span>
                                        </div>
                                        <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 shrink-0">
                                            {ings.length}
                                        </span>
                                    </button>

                                    {!isCollapsed && (
                                        <div className="border-t border-slate-100 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
                                            {ings.map((ing, index) => {
                                                const displayedQuantity = ing.quantity * scalingFactor;
                                                return (
                                                    <div
                                                        key={`${ing.name}-${index}`}
                                                        className={`flex items-start gap-3 px-4 py-3 ${ing.isMeat ? 'bg-rose-50/40 dark:bg-rose-900/20' : ''}`}
                                                    >
                                                        <span className={`flex-1 text-sm font-bold break-words ${ing.isMeat ? 'text-rose-900 dark:text-rose-200' : 'text-slate-700 dark:text-slate-200'}`}>
                                                            {ing.name}
                                                        </span>
                                                        <CookingQuantityCapsule
                                                            quantity={displayedQuantity}
                                                            unit={ing.unit}
                                                            t={t}
                                                            rtl={rtl}
                                                            isMeat={ing.isMeat}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-800 shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-full py-3 bg-brand-sage text-white font-black rounded-xl shadow-lg shadow-brand-sage/20 hover:opacity-90 transition-all text-sm uppercase tracking-widest"
                    >
                        {t('done')}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default RecipeIngredientsModal;
